import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {
  AppliedFilteredFields,
  AppliedSortingFields,
  ClinicPatientsAppliedFilters,
  ClinicPatientsResultsInspectedFilter,
  ClinicPatientsSortRule,
  FilteredSharedAccounts,
  PATIENTS_ACCOUNTS_ON_PAGE,
} from '../../symbols/dashboard-clinic-patients-manage-list.symbols';
import {User} from '../../../auth/symbols';

@Injectable()
export class DashboardClinicPatientsService {
  constructor(private http: HttpClient) {}

  /**
   * Return shared accounts data filtered by search string.
   */
  loadSharedAccountsData(
    search?: string,
    page = 1,
    sortingRule?: ClinicPatientsSortRule,
    filteringRule?: ClinicPatientsAppliedFilters
  ): Observable<FilteredSharedAccounts> {
    const url = `/api/research/patients-scans/`;

    // Should be sorted by upload date by default.
    let sortQuery: AppliedSortingFields = {upload_date: 'desc'};

    // Define sorting rule as it is provided by the backend.
    if (sortingRule === ClinicPatientsSortRule.NameAscend) {
      sortQuery = {name_sorting: 'asc'};
    } else if (sortingRule === ClinicPatientsSortRule.NameDescend) {
      sortQuery = {name_sorting: 'desc'};
    } else if (sortingRule === ClinicPatientsSortRule.ScanDateAscend) {
      sortQuery = {scan_date: 'asc'};
    } else if (sortingRule === ClinicPatientsSortRule.ScanDateDescend) {
      sortQuery = {scan_date: 'desc'};
    } else if (sortingRule === ClinicPatientsSortRule.UploadDateAscend) {
      sortQuery = {upload_date: 'asc'};
    } else if (sortingRule === ClinicPatientsSortRule.UploadDateDescend) {
      sortQuery = {upload_date: 'desc'};
    }

    // Define filtering rule as it is provided by the backend.
    let filterQuery: AppliedFilteredFields = {};
    if (filteringRule?.results_inspected === ClinicPatientsResultsInspectedFilter.inspected) {
      filterQuery.results_inspected = true;
    } else if (filteringRule?.results_inspected === ClinicPatientsResultsInspectedFilter.notInspected) {
      filterQuery.results_inspected = false;
    }

    return this.http.get<FilteredSharedAccounts>(url, {
      params: {
        name: search || '',
        page: page || 1,
        page_size: PATIENTS_ACCOUNTS_ON_PAGE.toString(),
        ...sortQuery,
        ...filterQuery,
      },
    });
  }

  /**
   * Remove relation between patient and his clinic.
   */
  removePatientFromClinic(patientId: User['uid']): Observable<void> {
    const url = `/api/patient-remove/${patientId}/`;
    return this.http.delete(url).pipe(map(() => null));
  }

  /**
   * Resend complete registration email for patient who has never logged in yet.
   */
  resendCompleteRegisterEmail(patientId: User['uid']): Observable<void> {
    const url = `/api/patient/resend-confirmation/`;
    return this.http.post(url, {patient_uid: patientId}).pipe(map(() => null));
  }
}
